/**
 * La lògica pura de la targeta d'una tasca de missatgeria (`MessagingTaskCardActions`): quan es pot
 * obrir l'enllaç de WhatsApp desat i què es fa amb la resposta de `/compose`. Sense React.
 *
 * Pagament (`PAYMENT_LINK`): mai l'enllaç desat (pot portar l'import i el token d'una sol·licitud
 * ja substituïda); sempre es recompon i, amb avisos o sense enllaç, WhatsApp no s'obre.
 */

/** El que la targeta mira d'una tasca (`OperationalTask` de `GET /operational-tasks`). */
export interface MessagingCardTask {
  id: string;
  title: string;
  body?: string;
  source: string;
  category: string;
  status: string;
  /** El kind del missatge, tal com arriba de `GET /operational-tasks`. */
  messageKind?: string | null;
  /** JSON amb les etiquetes (`["outbound","PAYMENT_LINK"]` a les tasques del botó de la fitxa). */
  tags?: string | null;
  whatsappLink?: string | null;
  sentMessageSnapshot?: string | null;
}

/** El que es mira de la resposta de `/compose`. */
export interface ComposeOutcome {
  text: string;
  whatsappLink: string;
  warnings?: string[] | null;
}

const PAYMENT_LINK_KIND = 'PAYMENT_LINK';

/** La tasca és d'un missatge de pagament (pel kind o, si no en porta, per l'etiqueta). */
function isPaymentLinkTask(task: Pick<MessagingCardTask, 'messageKind' | 'tags'>): boolean {
  if (task.messageKind) return task.messageKind === PAYMENT_LINK_KIND;
  try {
    const tags: unknown = JSON.parse(task.tags ?? '[]');
    return Array.isArray(tags) && tags.includes(PAYMENT_LINK_KIND);
  } catch {
    return false;
  }
}

/**
 * Es pot obrir l'enllaç de WhatsApp que ja tenim sense tornar a compondre? Per a un pagament, mai:
 * l'import i l'enllaç han de ser els de la sol·licitud oberta d'ARA.
 */
export function canOpenSavedLink(
  task: Pick<MessagingCardTask, 'messageKind' | 'tags'>,
  savedLink: string | null | undefined,
): boolean {
  return !!savedLink && !isPaymentLinkTask(task);
}

export type ComposeDecision =
  | { action: 'open'; whatsappLink: string; text: string }
  | { action: 'blocked'; warnings: string[] };

/**
 * Què es fa amb la resposta de `/compose`. Un pagament amb avisos (plantilla antiga, cap
 * sol·licitud oberta, enllaç marcat per revisar) o sense enllaç de WhatsApp no s'obre: es tornen
 * els avisos perquè es vegin. Els altres kinds s'obren com sempre.
 */
export function decideAfterCompose(
  task: Pick<MessagingCardTask, 'messageKind' | 'tags'>,
  result: ComposeOutcome,
): ComposeDecision {
  if (isPaymentLinkTask(task)) {
    const warnings = result.warnings ?? [];
    if (warnings.length > 0) return { action: 'blocked', warnings };
    if (!result.whatsappLink) {
      return { action: 'blocked', warnings: ['No hi ha cap enllaç de WhatsApp per obrir.'] };
    }
  }
  return { action: 'open', whatsappLink: result.whatsappLink, text: result.text };
}

const COMPOSE_ERROR = 'Error preparant el missatge';

/** El text d'un error de `/compose`: per a un pagament, el motiu que dona el backend. */
export function composeErrorText(
  task: Pick<MessagingCardTask, 'messageKind' | 'tags'>,
  error: unknown,
): string {
  if (!isPaymentLinkTask(task)) return COMPOSE_ERROR;
  const message = (error as { response?: { data?: { message?: unknown } } })?.response?.data
    ?.message;
  if (typeof message === 'string' && message.trim()) return message;
  if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
  return COMPOSE_ERROR;
}

/**
 * Per què un missatge de pagament acabat de preparar no es pot obrir a WhatsApp (regla ferma: amb
 * QUALSEVOL avís o sense enllaç, no s'envia), o `[]` si es pot o si no és de pagament. El fa
 * servir el diàleg del popover (`useWhatsAppSendFlow`).
 */
export function paymentSendBlock(
  messageKind: string | null | undefined,
  result: ComposeOutcome,
): string[] {
  const decision = decideAfterCompose({ messageKind }, result);
  return decision.action === 'blocked' ? decision.warnings : [];
}

/**
 * El text que es desa amb «Ja enviat (manual)». Per a un pagament, mai el `body` vell de la tasca
 * (pot dir l'import i l'enllaç d'una sol·licitud ja substituïda): es recompon i es desa el que
 * surt. Si ja no es pot compondre (la sol·licitud ja és pagada, la plantilla és antiga…), es diu
 * això en lloc de fer passar el text vell per enviat. Els altres kinds, el text de sempre.
 */
export async function resolveSentSnapshot(
  task: Pick<MessagingCardTask, 'messageKind' | 'tags'>,
  fallbackText: string,
  compose: () => Promise<ComposeOutcome>,
): Promise<string> {
  if (!isPaymentLinkTask(task)) return fallbackText;
  const unverified = (reason: string) =>
    `Marcat com enviat a mà; el text del missatge de pagament no s’ha pogut recompondre (${reason}).`;
  try {
    const decision = decideAfterCompose(task, await compose());
    return decision.action === 'open' ? decision.text : unverified(decision.warnings.join(' '));
  } catch (error) {
    return unverified(composeErrorText(task, error));
  }
}
